import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  CircleCheck,
  CircleX,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  pendingLabel?: string;
  onClick: () => void | Promise<void>;
}

export interface ToastInput {
  /** Plain outcome text. Use `action` for the single supported interaction. */
  message: string;
  variant?: ToastVariant;
  action?: ToastAction;
  /**
   * Milliseconds before dismissal. Use 0 for a persistent notification.
   * Errors and toasts with actions are persistent unless this is set.
   */
  durationMs?: number;
}

interface ToastRecord extends ToastInput {
  id: string;
  state: 'visible' | 'closing';
  variant: ToastVariant;
  durationMs: number;
}

export interface ToastApi {
  showToast: (input: ToastInput) => string;
  dismissToast: (id: string) => void;
}

const DEFAULT_TOAST_DURATION_MS = 6000;
const TOAST_EXIT_DURATION_MS = 200;
const MAX_VISIBLE_TOASTS = 4;

const ToastContext = createContext<ToastApi | null>(null);

const VARIANT_ICONS: Record<ToastVariant, LucideIcon> = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleX,
};

function resolvedDuration(input: ToastInput, variant: ToastVariant): number {
  if (input.durationMs !== undefined) return Math.max(0, input.durationMs);
  if (variant === 'error' || input.action) return 0;
  return DEFAULT_TOAST_DURATION_MS;
}

interface ToastItemProps {
  toast: ToastRecord;
  onDismiss: (id: string) => void;
}

/** The rendered notification. Most callers should use ToastProvider + useToast. */
export function Toast({ toast, onDismiss }: ToastItemProps) {
  const remainingMsRef = useRef(toast.durationMs);
  const startedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isActionPending, setIsActionPending] = useState(false);
  const Icon = VARIANT_ICONS[toast.variant];

  const pauseTimer = useCallback(() => {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    remainingMsRef.current = Math.max(
      0,
      remainingMsRef.current - (Date.now() - startedAtRef.current),
    );
  }, []);

  const startTimer = useCallback(() => {
    if (remainingMsRef.current <= 0 || timerRef.current !== null) return;
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onDismiss(toast.id);
    }, remainingMsRef.current);
  }, [onDismiss, toast.id]);

  useEffect(() => {
    remainingMsRef.current = toast.durationMs;
    startTimer();
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [startTimer, toast.durationMs]);

  const liveRole = toast.variant === 'error' ? 'alert' : 'status';
  const runAction = async () => {
    if (!toast.action || isActionPending) return;
    setIsActionPending(true);
    try {
      await toast.action.onClick();
      onDismiss(toast.id);
    } catch (error) {
      console.error('[Toast] Action failed:', error);
      setIsActionPending(false);
    }
  };

  return (
    <div
      className="toast"
      data-state={toast.state}
      data-variant={toast.variant}
      role={liveRole}
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
      onFocusCapture={pauseTimer}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) startTimer();
      }}
    >
      <Icon className="toast__icon" size={20} strokeWidth={1.5} aria-hidden="true" />
      <div className="toast__content">{toast.message}</div>
      {toast.action && (
        <button
          type="button"
          className="toast__action"
          disabled={isActionPending}
          aria-busy={isActionPending}
          aria-label={toast.action.label}
          onClick={() => { void runAction(); }}
        >
          {isActionPending ? toast.action.pendingLabel ?? toast.action.label : toast.action.label}
        </button>
      )}
      <button
        type="button"
        className="toast__dismiss"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
      >
        <X size={16} strokeWidth={1.5} aria-hidden="true" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextIdRef = useRef(0);
  const removalTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.map((toast) => (
      toast.id === id ? { ...toast, state: 'closing' } : toast
    )));

    if (removalTimersRef.current.has(id)) return;
    const timer = setTimeout(() => {
      removalTimersRef.current.delete(id);
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, TOAST_EXIT_DURATION_MS);
    removalTimersRef.current.set(id, timer);
  }, []);

  const showToast = useCallback((input: ToastInput) => {
    nextIdRef.current += 1;
    const id = `toast-${nextIdRef.current}`;
    const variant = input.variant ?? 'info';
    const record: ToastRecord = {
      ...input,
      id,
      variant,
      durationMs: resolvedDuration(input, variant),
      state: 'visible',
    };

    setToasts((current) => [...current, record].slice(-MAX_VISIBLE_TOASTS));
    return id;
  }, []);

  useEffect(() => () => {
    removalTimersRef.current.forEach(clearTimeout);
    removalTimersRef.current.clear();
  }, []);

  const api = useMemo(() => ({ showToast, dismissToast }), [dismissToast, showToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="toast-viewport" aria-label="Notifications">
          {toasts.map((toast) => (
            <Toast key={toast.id} toast={toast} onDismiss={dismissToast} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within <ToastProvider>');
  return context;
}
